import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useEffect, useMemo } from 'react';

import { SEARCH_DEGRADED_STATUS } from '../impls/docs-search-status';
import { AdvancedSearchService } from '../services/advanced-search';
import { CMDKQuickSearchService } from '../services/cmdk';
import { QuickSearchService } from '../services/quick-search';
import type { QuickSearchItem } from '../types/item';
import { AdvancedSearchDialog } from './advanced-search-dialog';
import { CMDK } from './cmdk';
import { groupQuickSearchItems } from './group-items';
import { QuickSearchModal } from './modal';

export const QuickSearchContainer = () => {
  const {
    quickSearchService,
    cMDKQuickSearchService,
    advancedSearchService,
    workspaceService,
  } = useServices({
    QuickSearchService,
    CMDKQuickSearchService,
    AdvancedSearchService,
    WorkspaceService,
  });
  const quickSearch = quickSearchService.quickSearch;
  const open = useLiveData(quickSearch.show$);
  const query = useLiveData(quickSearch.query$);
  const loading = useLiveData(quickSearch.isLoading$);
  const loadingProgress = useLiveData(quickSearch.loadingProgress$);
  const items = useLiveData(quickSearch.items$);
  const error = useLiveData(quickSearch.error$);
  const status = useLiveData(quickSearch.status$);
  const options = useLiveData(quickSearch.options$);
  const advancedSearchOpen = useLiveData(advancedSearchService.open$);
  const advancedSearchQuery = useLiveData(advancedSearchService.query$);
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const tagsEnabled = useTagsFeatureEnabled();
  const connectivityStatus = useConnectivityStatus();
  const hasQuery = query.trim().length > 0;
  const connectivitySearchDegraded = useUiOfflineSignal(
    open &&
      hasQuery &&
      workspaceService.workspace.flavour !== 'local' &&
      connectivityStatus.offline
  );
  const effectiveStatus = connectivitySearchDegraded
    ? SEARCH_DEGRADED_STATUS
    : status;
  const i18n = useI18n();

  const onToggleQuickSearch = useCallback(
    (open: boolean) => {
      if (open) {
        // should never be here
      } else {
        quickSearch.hide();
      }
    },
    [quickSearch]
  );

  const groups = useMemo(() => {
    return groupQuickSearchItems(items);
  }, [items]);

  const handleChangeQuery = useCallback(
    (query: string) => {
      quickSearch.setQuery(query);
    },
    [quickSearch]
  );

  useEffect(() => {
    if (advancedSearchOpen && open) {
      quickSearch.hide();
    }
  }, [advancedSearchOpen, open, quickSearch]);

  const handleSubmit = useCallback(
    (item: QuickSearchItem) => {
      quickSearch.submit(item);
    },
    [quickSearch]
  );

  const openAdvanced = useCallback(
    (initialQuery: string) => {
      quickSearch.hide();
      advancedSearchService.open(initialQuery);
    },
    [advancedSearchService, quickSearch]
  );

  const handleOpenAdvancedSearch = useCallback(() => {
    advancedSearchService.setPreferredView('advanced');
    openAdvanced(query);
  }, [advancedSearchService, openAdvanced, query]);

  const handleBackToNormal = useCallback(
    (currentQuery: string) => {
      advancedSearchService.setPreferredView('normal');
      advancedSearchService.close();
      cMDKQuickSearchService.openCompact(currentQuery, {
        collectionsEnabled,
        tagsEnabled,
      });
    },
    [
      advancedSearchService,
      cMDKQuickSearchService,
      collectionsEnabled,
      tagsEnabled,
    ]
  );

  return (
    <>
      <QuickSearchModal
        open={open && !advancedSearchOpen}
        onOpenChange={onToggleQuickSearch}
      >
        <CMDK
          query={query}
          groups={groups}
          error={error ? UserFriendlyError.fromAny(error).message : null}
          status={effectiveStatus ? i18n.t(effectiveStatus) : null}
          loading={loading}
          loadingProgress={loadingProgress}
          onQueryChange={handleChangeQuery}
          onSubmit={handleSubmit}
          onOpenAdvancedSearch={
            options?.allowAdvanced ? handleOpenAdvancedSearch : undefined
          }
          inputLabel={options?.label && i18n.t(options.label)}
          placeholder={options?.placeholder && i18n.t(options.placeholder)}
        />
      </QuickSearchModal>
      <AdvancedSearchDialog
        open={advancedSearchOpen}
        initialQuery={advancedSearchQuery}
        onOpenChange={open => {
          if (open) advancedSearchService.open(advancedSearchQuery);
          else advancedSearchService.close();
        }}
        onBackToNormal={handleBackToNormal}
      />
    </>
  );
};
